// @ts-strict-ignore
import Money from "@dashboard/components/Money";
import {
  OrderDetailsFragment,
  OrderErrorFragment,
  useOrderLinesMetadataLazyQuery,
} from "@dashboard/graphql";
import { useHasManageProductsPermission } from "@dashboard/orders/hooks/useHasManageProductsPermission";
import { useOrderLineDiscountContext } from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import { productPath } from "@dashboard/products/urls";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { makeStyles } from "@saleor/macaw-ui";
import {
  Box,
  ExternalLinkIcon,
  Skeleton,
  Text,
  TrashBinIcon,
} from "@saleor/macaw-ui-next";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { lineAlertMessages } from "../OrderDraftDetailsProducts/messages";
import { FormData } from "../OrderDraftDetailsProducts/OrderDraftDetailsProducts";

interface OrderDraftDetailsGroupedTableProps {
  lines: OrderDetailsFragment["lines"];
  errors: OrderErrorFragment[];
  loading: boolean;
  orderId?: string;
  onOrderLineChange: (id: string, data: FormData) => void;
  onOrderLineRemove: (id: string) => void;
  onShowMetadata?: (id: string) => void;
}

interface ProductGroup {
  productName: string;
  productId: string | null;
  thumbnailUrl: string | null;
  lines: OrderDetailsFragment["lines"];
}

interface MetadataItem {
  key: string;
  value: string;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      padding: theme.spacing(0, 3, 2),
    },
    productCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: theme.spacing(2),
      "&:last-child": {
        marginBottom: 0,
      },
    },
    productHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    thumbnail: {
      width: 44,
      height: 44,
      borderRadius: 6,
      objectFit: "cover",
      border: `1px solid ${theme.palette.divider}`,
      flexShrink: 0,
    },
    thumbnailPlaceholder: {
      width: 44,
      height: 44,
      borderRadius: 6,
      backgroundColor: theme.palette.divider,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    productInfo: {
      flex: 1,
      minWidth: 0,
    },
    productLink: {
      color: theme.palette.text.secondary,
      display: "inline-flex",
      alignItems: "center",
      marginLeft: "auto",
      flexShrink: 0,
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
    variantHeaderRow: {
      display: "grid",
      gridTemplateColumns: "1fr 150px 80px 110px 110px 40px minmax(120px, 1fr) 40px",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 2),
      borderBottom: `2px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f4f5f7",
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    },
    variantRow: {
      display: "grid",
      gridTemplateColumns: "1fr 150px 80px 110px 110px 40px minmax(120px, 1fr) 40px",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      alignItems: "center",
      borderBottom: `1px solid ${theme.palette.divider}`,
      transition: "background-color 150ms ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:last-child": {
        borderBottom: "none",
      },
    },
    variantName: {
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: "1.4",
      wordBreak: "break-word",
    },
    cellRight: {
      textAlign: "right",
    },
    cellCenter: {
      textAlign: "center",
    },
    quantityInput: {
      width: 60,
      padding: theme.spacing(0.5, 1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      textAlign: "center",
      fontSize: "0.875rem",
      outline: "none",
      "&:focus": {
        borderColor: theme.palette.primary.main,
      },
    },
    deleteButton: {
      cursor: "pointer",
      color: theme.palette.error.main,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(0.5),
      borderRadius: 4,
      transition: "background-color 150ms ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    metadataCell: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(0.5),
      alignItems: "center",
      cursor: "pointer",
      borderRadius: 6,
      padding: theme.spacing(0.5),
      transition: "background-color 150ms ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    headerLink: {
      color: theme.palette.primary.main,
      "&:hover": {
        textDecoration: "underline",
      },
    },
    metadataChip: {
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      padding: theme.spacing(0.75, 1.25),
      gap: theme.spacing(0.5),
      maxWidth: "100%",
    },
    metadataKey: {
      fontWeight: 600,
      fontSize: "1rem",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
    },
    metadataValue: {
      fontSize: "1rem",
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 200,
    },
    statusTag: {
      display: "inline-block",
      fontSize: "0.75rem",
      fontWeight: 500,
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 4,
      marginTop: theme.spacing(0.5),
    },
    statusError: {
      backgroundColor: theme.palette.error.light || "#fdecea",
      color: theme.palette.error.dark,
    },
    statusWarning: {
      backgroundColor: "#fff3e0",
      color: "#e65100",
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: "center",
    },
  }),
  { name: "OrderDraftDetailsGroupedTable" },
);

function groupLinesByProduct(
  lines: OrderDetailsFragment["lines"],
): ProductGroup[] {
  const groupMap = new Map<string, ProductGroup>();

  for (const line of lines) {
    const productId = line.variant?.product?.id ?? null;
    const key = productId ?? line.productName;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        productName: line.productName,
        productId,
        thumbnailUrl: line.thumbnail?.url ?? null,
        lines: [],
      });
    }

    const group = groupMap.get(key)!;

    group.lines.push(line);

    if (!group.thumbnailUrl && line.thumbnail?.url) {
      group.thumbnailUrl = line.thumbnail.url;
    }
  }

  return Array.from(groupMap.values());
}

const VariantRow = ({
  line,
  lineMetadata,
  lineError,
  optionSetLabel,
  classes,
  intl,
  onOrderLineChange,
  onOrderLineRemove,
  onShowMetadata,
}: {
  line: OrderDetailsFragment["lines"][number];
  lineMetadata?: MetadataItem[];
  lineError?: OrderErrorFragment;
  optionSetLabel?: string;
  classes: ReturnType<typeof useStyles>;
  intl: ReturnType<typeof useIntl>;
  onOrderLineChange: (id: string, data: FormData) => void;
  onOrderLineRemove: (id: string) => void;
  onShowMetadata?: (id: string) => void;
}) => {
  const getValues = useOrderLineDiscountContext();
  const { unitDiscountedPrice } = getValues(line.id);
  const [qty, setQty] = useState(line.quantity);

  useEffect(() => {
    setQty(line.quantity);
  }, [line.quantity]);

  const product = line.variant?.product;
  const statuses: Array<{ type: "error" | "warning"; label: string }> = [];

  if (lineError) {
    statuses.push({
      type: "error",
      label: getOrderErrorMessage(lineError, intl),
    });
  }

  if (!product) {
    statuses.push({
      type: "warning",
      label: intl.formatMessage(lineAlertMessages.notExists),
    });
  } else if (!product.isAvailableForPurchase) {
    statuses.push({
      type: "warning",
      label: intl.formatMessage(lineAlertMessages.notAvailable),
    });
  }

  return (
    <div className={classes.variantRow}>
      <div>
        <div className={classes.variantName}>
          <Text size={2}>{line.variant?.name ?? "-"}</Text>
        </div>
        {statuses.map((s, i) => (
          <span
            key={i}
            className={`${classes.statusTag} ${
              s.type === "error" ? classes.statusError : classes.statusWarning
            }`}
          >
            {s.label}
          </span>
        ))}
      </div>
      <Text size={2} color="default2">
        {optionSetLabel ?? "-"}
      </Text>
      <div className={classes.cellCenter}>
        <input
          type="number"
          min={1}
          className={classes.quantityInput}
          value={qty}
          onChange={e => setQty(Number(e.target.value))}
          onBlur={() => {
            if (qty !== line.quantity && qty > 0) {
              onOrderLineChange(line.id, { quantity: qty });
            }
          }}
          onKeyDown={e => {
            if (e.key === "Enter") {
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </div>
      <div className={classes.cellRight}>
        <Money money={unitDiscountedPrice} />
      </div>
      <div className={classes.cellRight}>
        <Money money={line.totalPrice.gross} />
      </div>
      <div />
      <div
        className={classes.metadataCell}
        onClick={onShowMetadata ? () => onShowMetadata(line.id) : undefined}
        title={intl.formatMessage({ id: 'N4ReBT', defaultMessage: "View Details" })}
      >
        {lineMetadata && lineMetadata.length > 0 ? (
          lineMetadata.map(item => (
            <div key={item.key} className={classes.metadataChip}>
              <span className={classes.metadataKey}>{item.key}:</span>
              <span className={classes.metadataValue} title={item.value}>
                {item.value}
              </span>
            </div>
          ))
        ) : (
          <Text size={2} className={classes.headerLink}>
            <FormattedMessage id="N4ReBT" defaultMessage="View Details" />
          </Text>
        )}
      </div>
      <div
        className={classes.deleteButton}
        onClick={() => onOrderLineRemove(line.id)}
        data-test-id="delete-order-line"
      >
        <TrashBinIcon />
      </div>
    </div>
  );
};

export const OrderDraftDetailsGroupedTable = ({
  lines,
  errors,
  loading,
  orderId,
  onOrderLineChange,
  onOrderLineRemove,
  onShowMetadata,
}: OrderDraftDetailsGroupedTableProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const groups = useMemo(() => groupLinesByProduct(lines), [lines]);
  const hasManageProducts = useHasManageProductsPermission();

  const [fetchMetadata, { data: metadataData }] =
    useOrderLinesMetadataLazyQuery();

  useEffect(() => {
    if (orderId && lines.length > 0) {
      fetchMetadata({
        variables: { id: orderId, hasManageProducts },
      });
    }
  }, [orderId, lines.length, fetchMetadata, hasManageProducts]);

  const { metadataByLineId, optionSetByLineId, compositeSkuByLineId } = useMemo(() => {
    const hiddenKeys = new Set([
      "cart_item_key",
      "is_option_line",
      "option_set_label",
      "option_set_name",
      "composite_sku",
      "line_discounted_unit_price",
      "line_original_unit_price",
      "option_is_additive",
      "option_value_sku",
    ]);
    const metaMap = new Map<string, MetadataItem[]>();
    const optionMap = new Map<string, string>();
    const compositeSkuMap = new Map<string, string>();

    if (metadataData?.order?.lines) {
      for (const line of metadataData.order.lines) {
        const optionSetLabel = (line.metadata ?? []).find(
          item => item.key === "option_set_label",
        );

        if (optionSetLabel) optionMap.set(line.id, optionSetLabel.value);

        const compositeSku = (line.metadata ?? []).find(
          item => item.key === "composite_sku",
        );

        if (compositeSku) compositeSkuMap.set(line.id, compositeSku.value);

        const filtered = (line.metadata ?? []).filter(
          item => !hiddenKeys.has(item.key) && item.value.trim() !== "",
        );

        if (filtered.length) metaMap.set(line.id, filtered);
      }
    }

    return { metadataByLineId: metaMap, optionSetByLineId: optionMap, compositeSkuByLineId: compositeSkuMap };
  }, [metadataData]);

  if (loading) {
    return (
      <Box padding={4}>
        <Skeleton />
      </Box>
    );
  }

  if (!lines.length) {
    return (
      <div className={classes.emptyState}>
        <Text color="default2" size={3}>
          <FormattedMessage
            id="Q1Uzbb"
            defaultMessage="No products found"
          />
        </Text>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {groups.map(group => {
        const groupCompositeSku =
          group.lines.reduce<string | null>(
            (acc, l) => acc ?? compositeSkuByLineId.get(l.id) ?? null,
            null,
          ) ?? group.lines[0]?.productSku ?? null;

        return (
        <div
          key={group.productId ?? group.productName}
          className={classes.productCard}
        >
          {/* Product header */}
          <div className={classes.productHeader}>
            {group.thumbnailUrl ? (
              <img
                src={group.thumbnailUrl}
                alt={group.productName}
                className={classes.thumbnail}
              />
            ) : (
              <div className={classes.thumbnailPlaceholder}>
                <Text size={1} color="default2">—</Text>
              </div>
            )}
            <div className={classes.productInfo}>
              <Text size={3} fontWeight="bold" display="block">
                {group.productName}
              </Text>
              <Text size={2} color="default2" display="block">
                {group.lines.length}{" "}
                {group.lines.length === 1 ? (
                  <FormattedMessage id="JWzoaP" defaultMessage="variant" />
                ) : (
                  <FormattedMessage id="mNvle2" defaultMessage="variants" />
                )}
              </Text>
              {groupCompositeSku && (
                <Text size={2} color="default2">
                  SKU: {groupCompositeSku}
                </Text>
              )}
            </div>
            {group.productId && (
              <Link
                to={productPath(group.productId)}
                target="_blank"
                className={classes.productLink}
              >
                <ExternalLinkIcon />
              </Link>
            )}
          </div>

          {/* Variant column headers */}
          <div className={classes.variantHeaderRow}>
            <Text size={2} color="default2" fontWeight="bold">
              <FormattedMessage id="OK5+Fh" defaultMessage="Variant" />
            </Text>
            <Text size={2} color="default2" fontWeight="bold">
              <FormattedMessage id="ITFMXZ" defaultMessage="Option Set" />
            </Text>
            <Text
              size={2}
              color="default2"
              fontWeight="bold"
              className={classes.cellCenter}
            >
              <FormattedMessage id="qVGRIE" defaultMessage="Quantity" />
            </Text>
            <Text
              size={2}
              color="default2"
              fontWeight="bold"
              className={classes.cellRight}
            >
              <FormattedMessage id="b1zuN9" defaultMessage="Price" />
            </Text>
            <Text
              size={2}
              color="default2"
              fontWeight="bold"
              className={classes.cellRight}
            >
              <FormattedMessage id="MJ2jZQ" defaultMessage="Total" />
            </Text>
            <div />
            <Text size={2} color="default2" fontWeight="bold" style={{ lineHeight: 1.3 }}>
              <FormattedMessage
                id="UOdOcz"
                defaultMessage="Additional{br}Information"
                values={{ br: <br /> }}
              />
            </Text>
            <div />
          </div>

          {/* Variant rows */}
          {group.lines.map(line => {
            const lineMetadata = metadataByLineId.get(line.id);
            const lineError = errors.find(error =>
              error.orderLines?.some(lineId => lineId === line.id),
            );

            return (
              <VariantRow
                key={line.id}
                line={line}
                lineMetadata={lineMetadata}
                lineError={lineError}
                optionSetLabel={optionSetByLineId.get(line.id)}
                classes={classes}
                intl={intl}
                onOrderLineChange={onOrderLineChange}
                onOrderLineRemove={onOrderLineRemove}
                onShowMetadata={onShowMetadata}
              />
            );
          })}
        </div>
        );
      })}
    </div>
  );
};
